import {
  Component,
  Suspense,
  useEffect,
  useState,
  type ErrorInfo,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import { ForfeoFeedbackState } from "./ForfeoFeedbackState";

type BoundaryProps = { children: ReactNode };
type BoundaryState = { hasError: boolean };

class ForfeoRouteErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { hasError: false };

  static getDerivedStateFromError(): BoundaryState {
    return { hasError: true };
  }

  componentDidCatch(_error: Error, _info: ErrorInfo) {
    // G2 n'affiche jamais les détails techniques ou les données de la route.
  }

  private retry = () => {
    this.setState({ hasError: false });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <main className="mx-auto flex min-h-[60vh] w-full max-w-4xl items-center px-4 py-12 sm:px-6">
          <ForfeoFeedbackState
            state="error"
            title="Cette page ne peut pas être affichée"
            description="Une erreur temporaire est survenue. Rechargez la page pour continuer."
            actionLabel="Recharger la page"
            onAction={this.retry}
          />
        </main>
      );
    }
    return this.props.children;
  }
}

export function ForfeoRouteBoundary({ children }: BoundaryProps) {
  return (
    <ForfeoRouteErrorBoundary>
      <Suspense
        fallback={(
          <main className="mx-auto flex min-h-[50vh] w-full max-w-4xl items-center px-4 py-12 sm:px-6">
            <ForfeoFeedbackState
              state="loading"
              title="Préparation de la page"
              description="Le contenu sera disponible dans quelques instants."
            />
          </main>
        )}
      >
        {children}
      </Suspense>
    </ForfeoRouteErrorBoundary>
  );
}

type NetworkState = "online" | "offline" | "recovered";

export function ForfeoRouteExperience() {
  const location = useLocation();
  const [announcement, setAnnouncement] = useState("");
  const [networkState, setNetworkState] = useState<NetworkState>(() => (
    typeof navigator === "undefined" || navigator.onLine ? "online" : "offline"
  ));

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      setAnnouncement(document.title.trim() || "Nouvelle page chargée");
    });
    return () => window.cancelAnimationFrame(frame);
  }, [location.pathname, location.search]);

  useEffect(() => {
    let recoveredTimer: number | undefined;
    const handleOffline = () => {
      if (recoveredTimer) window.clearTimeout(recoveredTimer);
      setNetworkState("offline");
    };
    const handleOnline = () => {
      setNetworkState((current) => current === "offline" ? "recovered" : "online");
      recoveredTimer = window.setTimeout(() => setNetworkState("online"), 4_000);
    };
    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);
    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
      if (recoveredTimer) window.clearTimeout(recoveredTimer);
    };
  }, []);

  return (
    <div data-forfeo-route-experience="active">
      <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {announcement}
      </p>
      {networkState === "offline" ? (
        <div className="fixed inset-x-3 top-3 z-[200] mx-auto max-w-xl sm:inset-x-6">
          <ForfeoFeedbackState
            state="offline"
            compact
            title="Connexion interrompue"
            description="Certaines informations peuvent être temporairement indisponibles."
            actionLabel="Réessayer"
            onAction={() => window.location.reload()}
          />
        </div>
      ) : null}
      {networkState === "recovered" ? (
        <p className="sr-only" role="status" aria-live="polite">
          Connexion rétablie.
        </p>
      ) : null}
    </div>
  );
}
